import { useState, useCallback, useEffect, useMemo } from "react";
import { useMatch } from "react-router-dom";

import { useModalContext } from "../../../../../../../_metronic/elements/Modal";
import { usePrescription } from "./PrescriptionContext";
import { openAlert } from "../../../../../../../_metronic/elements/NotificationAlert";
import { Loading } from "../../../../../../../_metronic/elements/Loading";
import { useErrorHandler } from "../../../../../../../_metronic/helpers/ErrorHandler";
import { useViewLandContext } from "../ViewLandContext";
import { useViewImageContext } from "../Images/ViewImageContext";
import { useViewAmbientContext } from "../Ambients";
import { format } from "date-fns";
import PrescriptionForm from "./PrescriptionForm";
import * as GisDataAdapter from "../../../services/GisDataAdapter";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../../../../../shared/responsive";
import { buildFieldLandDownloadSubject, buildPrescriptionDownloadBaseName, downloadBlob } from "../Layers/downloadArchive";
import { buildPrescriptionModel } from "./prescriptionModel";
import { buildPrescriptionReportPdfBlob } from "./prescriptionReportPdf";
import { PLANET_HD_V1_ENABLED } from "../../../../../../config/featureFlags";
import { isHdImageType } from "../Images/imageTypeUtils";
import { getExplicitRemoteSensingSource, withExplicitRemoteSensingSource } from "../Images/remoteSensingConsumerPayload";
import PrescriptionModalHeader from "./PrescriptionModalHeader";

const clsPresc = {
  0: 'mbp',
  1: 'bp',
  2: 'pp',
  3: 'ap',
  4: 'map',
  5: 'five',
  6: 'six'
};

const clsPrescInv = {
  'mbp': 0,
  'bp': 1,
  'pp': 2,
  'ap': 3,
  'map': 4,
  'five': 5,
  'six': 6
};

const ModalFooter = ({ canDownload, downloading, isMobile, onDownloadReport, reportDownloading }) => {
  const modal = useModalContext();
  const { t } = useTranslation();
  const isBusy = downloading || reportDownloading;

  return (
    <div className={`modal-footer prescription-modal__footer ${isMobile ? "prescription-modal__footer--mobile" : ""}`.trim()}>
      <button disabled={isBusy} type="button" className="btn btn-light prescription-modal__footer-button" onClick={() => modal.hideModal()}>
        {t("prescription:creation.buttons.cancel")}
      </button>
      <button
        disabled={isBusy || !canDownload}
        type="button"
        className="btn btn-light-primary prescription-modal__footer-button"
        data-kt-indicator={reportDownloading ? "on" : "off"}
        onClick={onDownloadReport}
      >
        <span className="indicator-label">
          {t("prescription:creation.buttons.download_report_pdf", {defaultValue: "Descargar reporte PDF"})}
        </span>
        <span className="indicator-progress">
          {t("prescription:creation.buttons.downloading_report_pdf", {defaultValue: "Generando PDF..."})}{" "}
          <span className="spinner-border spinner-border-sm align-middle ms-2" />
        </span>
      </button>
      <button
        disabled={isBusy || !canDownload}
        type="submit"
        className="btn btn-primary prescription-modal__footer-button"
        data-kt-indicator={downloading ? "on" : "off"}
      >
        <span className="indicator-label">
          {t("prescription:creation.buttons.download")}
        </span>
        <span className="indicator-progress">
          {t("prescription:creation.buttons.downloading")}{" "}
          <span className="spinner-border spinner-border-sm align-middle ms-2" />
        </span>
      </button>
    </div>
  )
}

const PrescriptionDialogClasses = ({ ambient, numClasses }) => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reportDownloading, setReportDownloading] = useState(false);
  const [prescription, setPrescription] = useState({});
  const { prod, units, columns, rowSpacingCm } = usePrescription();
  const { errorHandler } = useErrorHandler();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isAmbientActive = useMatch('/gis/field/view/:field_id/land/view/:land_id/ambients');
  const viewLandContext = useViewLandContext();
  const viewImageContext = useViewImageContext();
  const viewAmbientContext = useViewAmbientContext();

  const prescriptionModel = useMemo(() => {
    if (prescription == null || Object.keys(prescription).length < 1) {
      return null;
    }

    return buildPrescriptionModel({
      columns,
      numClasses,
      prescription,
      prod,
      rowSpacingCm,
      t,
      units,
    });
  }, [columns, numClasses, prescription, prod, rowSpacingCm, t, units]);

  const hasCompleteProductColumn = (prescriptionModel?.productSummaries?.length || 0) > 0;

  const load = useCallback(async () => {
    setLoading(true);

    const presc = {
      map: { ha: 0 },
      ap: { ha: 0 },
      pp: { ha: 0 },
      bp: { ha: 0 },
      mbp: { ha: 0 },
      five: { ha: 0 },
      six: { ha: 0 }
    };

    if (!isAmbientActive || !ambient) {
      const imageLand = viewImageContext.getLand(viewLandContext.land.land_id);

      if (imageLand == null) {
        return;
      }

      const isHdSelection = isHdImageType(imageLand.imageType);
      if (isHdSelection) {
        openAlert(
          "danger",
          t("prescription:creation.errors.title"),
          t("prescription:creation.errors.image_ambient_hd_required", {
            defaultValue: "Primero creá y seleccioná una ambientación HD por imagen terminada.",
          })
        );
        setPrescription({});
        setLoading(false);
        return;
      }
      const classificationPayload = {
        layer: imageLand['imageType'] == 'evi' ? 'evi' : 'ndvi',
        date: format(imageLand['selectedDate'], 'yyyy-MM-dd'),
        num_classes: numClasses
      };
      const res = await errorHandler(
        GisDataAdapter.classifyLandPrescription(viewLandContext.land.land_id, classificationPayload)
      );

      for (var z in res.data.classes) {
        const { cls, sup_ha } = res.data.classes[z];
        presc[clsPresc[cls]]['ha'] += sup_ha;
        presc[clsPresc[cls]]['cls'] = cls;
      }
    } else {
      ambient.geojson.features.forEach(e => {
        presc[clsPresc[e.properties.cls]]['ha'] += e.properties.sup_ha;
        presc[clsPresc[e.properties.cls]]['cls'] = e.properties.cls;
      });
    }

    setPrescription(presc);
    setLoading(false);
  }, [ambient, viewLandContext.land, isAmbientActive]);

  useEffect(() => {
    load();
  }, [ambient]);

  const handleDownload = async (e) => {
    e.preventDefault();

    if (!hasCompleteProductColumn) {
      openAlert('danger', t("prescription:creation.errors.title"), t("prescription:creation.errors.missing_at_least_column"));
      return;
    }

    setDownloading(true);
    const validPrescription = Object.keys(prescription).filter(e => prescription[e]['ha'] > 0).length;
    const data = [];

    const dataName = { 0: 'prod1', 1: 'prod2', 2: 'prod3' };
    const validColumns = [];

    for (var i = 0; i < validPrescription; i++) {
      validColumns.push(clsPresc[i]);
    }

    for (var z in columns) {
      let valid = true;

      Object.keys(prescription).filter(e => prescription[e]['ha'] > 0).forEach(e => {
        const currentCls = prescription[e]['cls'];
        if (!columns[z][currentCls] || columns[z][currentCls].length < 1) {
          valid = false;
        }
      });

      if (valid && Object.keys(columns[z]).length >= validPrescription) {
        data.push({
          name: prod[dataName[z]] || `Prod ${z + 1}`,
          unit: units[dataName[z]],
          values: Object.keys(columns[z]).map(key => {
            return {
              'cls': key,
              'value': parseFloat(columns[z][key])
            };
          })
        });
      }
    }

    if (data.length < 1) {
      openAlert('danger', t("prescription:creation.errors.title"), t("prescription:creation.errors.missing_at_least_column"));
      setDownloading(false);
      return;
    }

    let res;
    let prescriptionDownloadName = null;

    if (!ambient || !isAmbientActive) {
      const imageLand = viewImageContext.getLand(viewLandContext.land.land_id);
      if (isHdImageType(imageLand?.imageType)) {
        openAlert(
          "danger",
          t("prescription:creation.errors.title"),
          t("prescription:creation.errors.image_ambient_hd_required", {
            defaultValue: "Primero creá y seleccioná una ambientación HD por imagen terminada.",
          })
        );
        setDownloading(false);
        return;
      }
      const totalRequest = withExplicitRemoteSensingSource({
        layer: imageLand['imageType'] == 'evi' ? 'evi' : 'ndvi',
        date: format(imageLand['selectedDate'], 'yyyy-MM-dd'),
        num_classes: numClasses,
        driver: 'ESRI Shapefile',
        data: data
      }, imageLand);
      res = await errorHandler(GisDataAdapter.downloadLandPrescription({ landId: viewLandContext.land.land_id, data: totalRequest }));
    } else {
      const totalRequest = withExplicitRemoteSensingSource({
        driver: 'ESRI Shapefile',
        data: data
      }, ambient, {ambientType: viewAmbientContext.selectedElement});
      if (viewAmbientContext.selectedElement === 'ambient'){
        prescriptionDownloadName = buildPrescriptionDownloadBaseName({
          prefix: "Prescripción",
          fieldName: viewLandContext.land.field.name,
          landName: viewLandContext.land.name,
        });
        res = await errorHandler(GisDataAdapter.downloadLandPrescriptionAmbient({ landId: viewLandContext.land.land_id, data: totalRequest }));
      }
      else if (viewAmbientContext.selectedElement === 'imageAmbient'){
        prescriptionDownloadName = buildPrescriptionDownloadBaseName({
          prefix: "Prescripción UI",
          fieldName: viewLandContext.land.field.name,
          landName: viewLandContext.land.name,
        });
        res = await errorHandler(GisDataAdapter.downloadLandPrescriptionImageAmbient({ landId: viewLandContext.land.land_id, data: totalRequest }));
      }
      else if (viewAmbientContext.selectedElement === 'ambientHd') {
        prescriptionDownloadName = buildPrescriptionDownloadBaseName({
          prefix: "Prescripción HD",
          fieldName: viewLandContext.land.field.name,
          landName: viewLandContext.land.name,
        });
        res = await errorHandler(GisDataAdapter.downloadLandPrescriptionAmbientHd({landId: viewLandContext.land.land_id, data: totalRequest}));
      }
      else if (viewAmbientContext.selectedElement === 'imageAmbientHd') {
        prescriptionDownloadName = buildPrescriptionDownloadBaseName({
          prefix: "Prescripción UI HD",
          fieldName: viewLandContext.land.field.name,
          landName: viewLandContext.land.name,
        });
        res = await errorHandler(GisDataAdapter.downloadLandPrescriptionImageAmbientHd({landId: viewLandContext.land.land_id, data: totalRequest}));
      }
      
      
    }

    if (res.ok) {
      downloadBlob({
        blob: new Blob([res.data], { type: "application/zip" }),
        fileName: prescriptionDownloadName
          ? `${prescriptionDownloadName}.zip`
          : t("prescription:creation.success.filename"),
      });

      openAlert('success', t("prescription:creation.success.title"), t("prescription:creation.success.message"));
    }

    setDownloading(false);
  }

  const handleDownloadReport = async () => {
    if (!prescriptionModel || !hasCompleteProductColumn) {
      openAlert('danger', t("prescription:creation.errors.title"), t("prescription:creation.errors.missing_at_least_column"));
      return;
    }

    setReportDownloading(true);

    try {
      const fieldName = viewLandContext.land.field.name;
      const landName = viewLandContext.land.name;
      const subject = buildFieldLandDownloadSubject({fieldName, landName});
      const reportDownloadName = buildPrescriptionDownloadBaseName({
        prefix: "Reporte prescripción",
        fieldName,
        landName,
      });
      const blob = await buildPrescriptionReportPdfBlob({
        ambient,
        fieldName,
        landName,
        model: prescriptionModel,
        rowSpacingCm,
        subject,
        remoteSensingSource: PLANET_HD_V1_ENABLED
          ? getExplicitRemoteSensingSource(
              ambient || viewImageContext.getLand(viewLandContext.land.land_id) || {}
            )
          : null,
        t,
      });

      downloadBlob({
        blob,
        fileName: `${reportDownloadName}.pdf`,
      });
    } finally {
      setReportDownloading(false);
    }
  };

  if (loading) {
    return <div className="p-5"><Loading /></div>;
  }

  if (prescription == null || Object.keys(prescription).length < 1) {
    return (<></>);
  }

  return (
    <>
      <form onSubmit={handleDownload} className='form'>
        <PrescriptionModalHeader
          disabled={downloading || reportDownloading}
          isMobile={isMobile}
          titleKey="prescription:creation.title"
        />
        <div className={`modal-body prescription-modal__body ${isMobile ? "prescription-modal__body--mobile" : ""}`.trim()}>
          {!loading && <PrescriptionForm ambient={ambient} numClasses={numClasses} prescription={prescription} />}
        </div>
        <ModalFooter
          canDownload={hasCompleteProductColumn}
          downloading={downloading}
          isMobile={isMobile}
          onDownloadReport={handleDownloadReport}
          reportDownloading={reportDownloading}
        />
      </form>
    </>
  );
};

export default PrescriptionDialogClasses;
